import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { PagePlaceholder } from "@/components/common/PagePlaceholder";
import { api } from "@/features/auth/api";

type Product = {
    id: string;
    slug: string;
    name: string;
    brand: string;
    price: number;
    mrp: number;
    weight: string;
    category_slug: string;
};

export default function Search() {
    const [term, setTerm] = useState("");
    const [debounced, setDebounced] = useState("");

    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(term.trim()), 250);
        return () => window.clearTimeout(t);
    }, [term]);

    const enabled = debounced.length >= 2;
    const { data, isFetching } = useQuery({
        queryKey: ["search", debounced],
        enabled,
        queryFn: async () => {
            const { data } = await api.get<{ items: Product[]; total: number }>(
                `/products?search=${encodeURIComponent(debounced)}&page_size=24`,
            );
            return data;
        },
    });

    return (
        <div data-testid="search-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <h1 className="tsgo-h1">Search</h1>
                <p className="tsgo-lead mt-2">Find products, brands, and more.</p>
                <div className="relative mt-6 max-w-2xl">
                    <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder='Try "milk", "chips" or "shampoo"'
                        data-testid="search-input"
                        className="h-12 rounded-full pl-12 text-base"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        autoFocus
                    />
                </div>
            </section>

            <section className="tsgo-container mt-8">
                {!enabled ? (
                    <PagePlaceholder
                        testId="search-empty"
                        icon={<SearchIcon className="h-6 w-6" />}
                        title="Start typing to search"
                        description="Type at least 2 characters."
                    />
                ) : isFetching && !data ? (
                    <p className="text-sm text-muted-foreground">Searching…</p>
                ) : (data?.items ?? []).length === 0 ? (
                    <PagePlaceholder
                        testId="search-noresult"
                        icon={<SearchIcon className="h-6 w-6" />}
                        title={`No results for "${debounced}"`}
                        description="Try a different word or check the spelling."
                    />
                ) : (
                    <ul
                        data-testid="search-results"
                        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        {(data?.items ?? []).map((p) => (
                            <li key={p.id}>
                                <Link
                                    to={`/product/${p.slug}`}
                                    data-testid={`search-result-${p.slug}`}
                                    className="block rounded-2xl border border-border/70 bg-card p-4 hover:border-brand"
                                >
                                    <p className="font-bold">{p.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {p.brand} · {p.weight}
                                    </p>
                                    <p className="mt-2 text-sm font-bold text-brand">
                                        ₹{Math.round(p.price / 100)}
                                    </p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
